export class AuthClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = this.getAuthBaseUrl();
  }

  private getAuthBaseUrl(): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3002';
    }
    
    return `${protocol}//${hostname}`;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  isLoggedIn(): boolean {
    return !!sessionStorage.getItem('token');
  }

  getToken(): string | null {
    // 優先從 sessionStorage 讀取，如果沒有則從 localStorage 讀取
    return sessionStorage.getItem('token') || localStorage.getItem('token');
  }

  getCurrentUser(): any {
    // 優先從 sessionStorage 讀取，如果沒有則從 localStorage 讀取
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  async login(username: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      // 清除所有舊的 cookies (通過發送請求到後端)
      document.cookie.split(";").forEach((c) => {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      
      const response = await fetch(`${this.baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success) {
        // 清除舊版本的全域快取 (不按用戶區分的舊資料)
        localStorage.removeItem('echov-models');
        localStorage.removeItem('echov-folders');
        localStorage.removeItem('robim-models');
        localStorage.removeItem('robim-folders');
        
        // 同時存到 sessionStorage 和 localStorage，確保跨視窗可用
        sessionStorage.setItem('token', data.token);
        sessionStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        console.log('✅ Login successful');
      }

      return data;
    } catch (error) {
      console.error('❌ Login error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async logout(): Promise<void> {
    this.clearAllCache();
    localStorage.setItem('logout-event', Date.now().toString());
    window.location.href = '/login.html';
  }
  
  clearAllCache(): void {
    // 獲取當前用戶 ID
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    let userId = null;
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        userId = user.userId;
      } catch (e) {
        console.error('❌ Error parsing user:', e);
      }
    }
    
    // 清除認證資料
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // 清除當前用戶的快取資料
    if (userId) {
      localStorage.removeItem(`echov-models-${userId}`);
      localStorage.removeItem(`echov-folders-${userId}`);
      console.log('✅ Cache cleared');
    }
    
    // 清除舊版本的全域快取 (不按用戶區分的舊資料)
    localStorage.removeItem('echov-models');
    localStorage.removeItem('echov-folders');
    localStorage.removeItem('robim-models');
    localStorage.removeItem('robim-folders');
  }

  logoutAndRedirect(): void {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login.html';
  }

  async getUserFolders(): Promise<any[]> {
    try {
      const token = this.getToken();
      
      if (!token) {
        console.error('❌ No authentication token');
        return [];
      }

      const response = await this.fetchWithAuth('/api/folders');

      const data = await response.json();
      return data.success ? data.folders : [];
    } catch (error) {
      console.error('❌ Failed to load folders:', error);
      return [];
    }
  }

  async saveUserFolders(folders: any[]): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) return false;

      const response = await fetch(`${this.baseUrl}/api/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ folders })
      });

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('❌ [AuthClient.saveUserFolders] Error:', error);
      return false;
    }
  }

  async getUserModels(): Promise<any[]> {
    try {
      const token = this.getToken();
      
      if (!token) {
        console.error('❌ No authentication token');
        return [];
      }

      const response = await this.fetchWithAuth('/api/models');

      const data = await response.json();
      return data.success ? data.models : [];
    } catch (error) {
      console.error('❌ Failed to load models:', error);
      return [];
    }
  }

  async saveUserModels(models: any[]): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) return false;

      const response = await fetch(`${this.baseUrl}/api/models`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ models })
      });

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('❌ [AuthClient.saveUserModels] Error:', error);
      return false;
    }
  }

  /**
   * 保存單個模型配置到 MongoDB
   */
  async saveUserModel(model: any): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('未登入或 token 已過期');
      }

      const response = await fetch(`${this.baseUrl}/api/models/single`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(model)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('❌ [AuthClient.saveUserModel] Error:', error);
      throw error;
    }
  }

  /**
   * 保存單個 folder 到 MongoDB
   */
  async saveUserFolder(folder: any): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('未登入或 token 已過期');
      }

      const response = await fetch(`${this.baseUrl}/api/folders/single`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(folder)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        console.error(`❌ [AuthClient.saveUserFolder] Response error:`, errorData);
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('❌ [AuthClient.saveUserFolder] Error:', error);
      throw error;
    }
  }

  /**
   * 刪除單個模型從 MongoDB
   */
  async deleteUserModel(modelId: string): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('未登入或 token 已過期');
      }

      const response = await fetch(`${this.baseUrl}/api/models/${modelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('❌ [AuthClient.deleteUserModel] Error:', error);
      throw error;
    }
  }

  /**
   * 刪除單個 folder 從 MongoDB
   */
  async deleteUserFolder(folderId: string): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('未登入或 token 已過期');
      }

      const response = await fetch(`${this.baseUrl}/api/folders/${folderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('❌ [AuthClient.deleteUserFolder] Error:', error);
      throw error;
    }
  }

  /**
   * 通用的帶認證的 fetch 方法
   */
  async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getToken();
    const user = this.getCurrentUser();
    
    if (!token) {
      console.error('❌ No authentication token');
      throw new Error('Not logged in or token expired');
    }
    
    if (!user || !user.userId) {
      console.error('❌ Invalid user session');
      throw new Error('Invalid user session');
    }

    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    
    const headers = new Headers(options.headers);
    headers.set('Authorization', `Bearer ${token}`);
    
    return fetch(fullUrl, {
      ...options,
      headers
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.fetchWithAuth('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Change password error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async refreshUserInfo(): Promise<void> {
    try {
      const response = await this.fetchWithAuth('/api/auth/me');
      const data = await response.json();
      
      if (data.success && data.user) {
        sessionStorage.setItem('user', JSON.stringify(data.user));
      }
    } catch (error) {
      console.error('❌ Refresh user info error:', error);
    }
  }

  async deleteAccount(password?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.fetchWithAuth('/api/auth/account', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await response.json();
      
      if (data.success) {
        // 清除所有資料
        this.clearAllCache();
      }
      
      return data;
    } catch (error) {
      console.error('❌ Delete account error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async deleteFolder(folderId: string): Promise<{ success: boolean; error?: string; deleted?: { folders: number; models: number; markers: number } }> {
    try {
      const response = await this.fetchWithAuth(`/api/folders/${folderId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Delete folder error:', error);
      return { success: false, error: 'Network error' };
    }
  }
}

export function getAuthBaseUrl(): string {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3002';
  }
  
  return `${protocol}//${hostname}`;
}

// 創建全局 authClient 實例
export const authClient = new AuthClient();
